/**
 * MONSTER TOWN — VISIT CREW PICTURES. VISIT CREW (src/visit/**).
 *
 * LAW #4: NO READING REQUIRED. Four meanings this feature needs that the HUD's
 * icon set does not have a word — sorry, a picture — for:
 *
 *   gate      the way IN to the other kid's town (an open door with a step)
 *   present   a wrapped box with a ribbon. The one picture the whole gift half
 *             of the feature rests on, so it is drawn big and unmistakable.
 *   goHome    a house with an arrow pointing into it. THE WAY OUT. It is the
 *             single most important picture in this crew: a 3-year-old who
 *             cannot find it is stuck in somebody else's town.
 *   postcard  a photo you brought back.
 *
 * Drawn to the same rules as `src/ui/icons.ts` so nothing looks like it came
 * from a different game: one viewBox (0 0 100 100), one ink colour, stroke 7 for
 * silhouette and 5 for detail, round caps, fills from `palette.ts`, and ZERO
 * external assets. They are strings of SVG generated in code.
 *
 * They live here, and not in the HUD's set, because file ownership is law: this
 * crew owns `src/visit/**` and nothing else. The DOM shape produced is identical
 * to `iconEl()`'s — a `.mt-ico` span sized inline in three places for exactly
 * the reason documented in `src/ui/icons.ts` (an svg with only a viewBox has no
 * intrinsic size and inflates to its container if a stylesheet is late).
 */

import { css, type PaletteName } from '../core/palette'

const INK = css('ink')
const S = `stroke="${INK}" stroke-linecap="round" stroke-linejoin="round"`
const OUT = `${S} stroke-width="7"`
const DET = `${S} stroke-width="5"`

interface Pen {
  f?: PaletteName
  g?: PaletteName
}

const fill = (p: Pen, fallback: PaletteName): string => css(p.f ?? fallback)
const fill2 = (p: Pen, fallback: PaletteName): string => css(p.g ?? fallback)

const DRAW: Record<string, (p: Pen) => string> = {
  /** The way in: a door standing open, with a doormat in front of it. */
  gate: (p) => `
    <path d="M24 20 H70 V84 H24 Z" fill="${fill(p, 'doorWood')}" ${OUT}/>
    <path d="M70 20 L86 30 V74 L70 84 Z" fill="${fill2(p, 'woodLight')}" ${OUT}/>
    <circle cx="34" cy="54" r="4.5" fill="${INK}"/>
    <path d="M14 90 H62" ${OUT} fill="none"/>`,

  /** A wrapped present. The whole gift half of this feature rests on it. */
  present: (p) => `
    <path d="M16 44 H84 V86 H16 Z" fill="${fill(p, 'flowerPink')}" ${OUT}/>
    <path d="M12 30 H88 V46 H12 Z" fill="${fill2(p, 'monsterSunny')}" ${OUT}/>
    <path d="M50 30 V86" ${DET} fill="none"/>
    <path d="M50 30 C50 30 34 8 26 18 C20 26 38 30 50 30 Z" fill="${fill2(p, 'monsterSunny')}" ${DET}/>
    <path d="M50 30 C50 30 66 8 74 18 C80 26 62 30 50 30 Z" fill="${fill2(p, 'monsterSunny')}" ${DET}/>`,

  /** THE WAY OUT. A house with an arrow walking into it. */
  goHome: (p) => `
    <path d="M50 14 L88 46 H74 V84 H26 V46 H12 Z" fill="${fill(p, 'roofRed')}" ${OUT}/>
    <path d="M40 84 V58 H60 V84" fill="${fill2(p, 'doorWood')}" ${OUT}/>
    <path d="M4 70 H30" ${OUT} fill="none"/>
    <path d="M20 60 L32 70 L20 80" ${OUT} fill="none"/>`,

  /** A picture you brought home. */
  postcard: (p) => `
    <path d="M12 24 H88 V78 H12 Z" fill="${fill(p, 'uiPanel')}" ${OUT}/>
    <path d="M20 66 L38 44 L52 60 L62 52 L80 66 Z" fill="${fill2(p, 'grass')}" ${DET}/>
    <circle cx="70" cy="38" r="7" fill="${css('monsterSunny')}" ${DET}/>`,

  /**
   * THE MAT ITSELF, woven, lying on their step — for the EMPTY doorstep.
   *
   * It used to be the `present` glyph greyed out inside a dashed box, which is
   * the single most literal "disabled HTML form control" shape in the build: a
   * pre-reader reads grey-on-cream as broken, and the same present picture then
   * appeared twice on one screen at two sizes and two colours meaning two
   * different things (nothing here / leave something here). A mat with nothing
   * on it is a mat, not a switched-off present.
   *
   * NOT DRAWN ON THE DOORSTEP ANY MORE, and the reason is worth keeping: a 72 px
   * picture of a mat, centred in a 446 px row that was not a mat, is two objects
   * making one point badly ("a wide cream pill with one small doormat icon lost
   * in the middle"). The ROW is woven now — `.mt-visit-mat`, visit.css — so the
   * mat is drawn at the size a mat is. Kept here, unused, exactly as `postcard`
   * and `emptyBag` are: this is a small picture library for one feature, and a
   * meaning this feature may need again should not have to be redrawn.
   */
  doormat: (p) => `
    <path d="M12 32 H88 V78 H12 Z" fill="${fill(p, 'woodLight')}" ${OUT}/>
    <path d="M12 44 H88 M12 55 H88 M12 66 H88" ${DET} fill="none" opacity="0.55"/>
    <path d="M30 32 V78 M50 32 V78 M70 32 V78" ${DET} fill="none" opacity="0.35"/>
    <path d="M6 84 H94" ${OUT} fill="none"/>`,

  /** An empty bag — "nothing in here yet", never an error state. */
  emptyBag: (p) => `
    <path d="M26 38 H74 L80 84 H20 Z" fill="${fill(p, 'woodLight')}" ${OUT}/>
    <path d="M38 40 V28 A12 12 0 0 1 62 28 V40" ${OUT} fill="none"/>
    <path d="M44 60 L56 72 M56 60 L44 72" ${DET} fill="none" opacity="0.45"/>`,
}

export function visitIconSvg(name: string, pen: Pen = {}): string {
  const draw = DRAW[name]
  const body = draw === undefined ? '' : draw(pen)
  return `<svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" focusable="false" aria-hidden="true">${body}</svg>`
}

export function hasVisitIcon(name: string): boolean {
  return name in DRAW
}

/**
 * An icon as a DOM element sized in CSS px. Sized inline in three places on
 * purpose — see the long note in `src/ui/icons.ts`; a stylesheet that lands one
 * frame late must not be able to turn a 60 px picture into an 800 px one.
 */
export function visitIconEl(name: string, sizePx: number, pen: Pen = {}): HTMLSpanElement {
  const span = document.createElement('span')
  span.className = 'mt-ico'
  span.style.display = 'block'
  span.style.flex = 'none'
  span.style.width = `${sizePx}px`
  span.style.height = `${sizePx}px`
  span.innerHTML = visitIconSvg(name, pen)
  const svg = span.firstElementChild
  if (svg !== null) {
    svg.setAttribute('width', '100%')
    svg.setAttribute('height', '100%')
  }
  return span
}
